import axios from "axios";

const instance = axios.create({
	baseURL: process.env.REACT_APP_API_URL,
	headers: {
		"Content-Type": "application/json",
	},
});

// ---------------------------------------------------------------------------
// Superapp WebView token-refresh bridge
// ---------------------------------------------------------------------------
// This web app runs inside the ANSA superapp's WebView with an access token supplied
// via the page URL (?token=...). By design it holds NO refresh token — putting one in
// the web layer's URL/localStorage would reintroduce the very exposure WAPT L5 closed.
// So when the URL-supplied access token expires mid-session (short 45m TTL, WAPT L6),
// we ask the HOST superapp — which owns the refresh token — to mint a fresh access
// token and inject it back via window.__ansaSetToken, then transparently retry the
// failed request. Outside a WebView (plain browser) none of this runs.
const inWebView = () =>
	typeof window !== "undefined" && !!window.ReactNativeWebView;

let __ansaTokenWaiters = [];

if (typeof window !== "undefined") {
	// Invoked by the host superapp (via injectJavaScript) with a fresh access token.
	window.__ansaSetToken = (token) => {
		try {
			if (token) localStorage.setItem("ansa_token", JSON.stringify(token));
		} catch (e) {}
		const waiters = __ansaTokenWaiters;
		__ansaTokenWaiters = [];
		waiters.forEach((resolve) => resolve(token || null));
	};
}

function requestFreshTokenFromHost() {
	return new Promise((resolve) => {
		__ansaTokenWaiters.push(resolve);
		try {
			window.ReactNativeWebView.postMessage("ANSA_TOKEN_EXPIRED");
		} catch (e) {
			resolve(null);
		}
		// Fail-safe: if the host never answers, stop waiting so the original 401 surfaces.
		setTimeout(() => {
			const i = __ansaTokenWaiters.indexOf(resolve);
			if (i !== -1) {
				__ansaTokenWaiters.splice(i, 1);
				resolve(null);
			}
		}, 15000);
	});
}

instance.interceptors.response.use(
	(response) => response,
	async (error) => {
		const status = error?.response?.status;
		const config = error?.config;
		// Only bridge genuine auth expiries inside the superapp WebView, and retry once.
		if (status === 401 && inWebView() && config && !config.__ansaRetried) {
			config.__ansaRetried = true;
			const token = await requestFreshTokenFromHost();
			if (token) {
				config.headers = config.headers || {};
				config.headers.Authorization = `Bearer ${token}`;
				return instance(config);
			}
		}
		return Promise.reject(error);
	}
);

export default instance;
